import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import { Plus, Search, Edit, Trash2, Calendar, FileText, Users, ChevronDown, ChevronUp, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MesNotes = () => {
  const { getPatientLabel, getPatientLabelPlural, getPatientLabelCapitalized, getPatientArticle, areStatusesEnabled } = useSettings();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [patients, setPatients] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState('all');
  const [selectedGroupe, setSelectedGroupe] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [isCreateNoteOpen, setIsCreateNoteOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editKey, setEditKey] = useState(0); // Key to force RichTextEditor remount
  const [isEditorReady, setIsEditorReady] = useState(false); // Flag to show editor only when ready
  const [loading, setLoading] = useState(true);
  const [noteStatuses, setNoteStatuses] = useState([]); // Note statuses

  // Form states
  const [newNote, setNewNote] = useState({
    patient_id: '',
    titre: '',
    contenu: '',
    status_id: ''
  });
  
  const [editNote, setEditNote] = useState({
    titre: '',
    contenu: '',
    status_id: ''
  });
  // Expansion states for "read more" functionality
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  
  // Comments states
  const [noteComments, setNoteComments] = useState({}); // Map of note_id -> comments array
  const [expandedComments, setExpandedComments] = useState(new Set()); // Set of note IDs with expanded comments
  const [newComment, setNewComment] = useState({});  // Map of note_id -> comment content
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Fixed at 10 items per page

  // Create a map of groupe IDs to names for fast lookup
  const groupeMap = useMemo(() => {
    const map = {};
    groupes.forEach(g => {
      map[g.id] = g.nom;
    });
    return map;
  }, [groupes]);

  // Load data
  const loadData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      
      const [patientsRes, groupesRes] = await Promise.all([
        axios.get(`${API}/patients`),
        axios.get(`${API}/groupes`)
      ]);
      
      setPatients(patientsRes.data);
      setGroupes(groupesRes.data);
      
      // Load all notes only if we have patients
      if (patientsRes.data.length > 0) {
        const notesPromises = patientsRes.data.map(patient => 
          axios.get(`${API}/patients/${patient.id}/notes`).catch(() => ({ data: [] }))
        );
        
        const notesResults = await Promise.all(notesPromises);
        const allNotes = notesResults.flatMap((result, index) => 
          result.data.map(note => ({ ...note, patient: patientsRes.data[index] }))
        );
        
        // Remove duplicates based on note ID
        const uniqueNotes = allNotes.filter((note, index, self) => 
          index === self.findIndex(n => n.id === note.id)
        );
        
        setNotes(uniqueNotes);
        
        // Cache the data
        try {
          sessionStorage.setItem('cachedNotesData', JSON.stringify({
            data: {
              notes: uniqueNotes,
              patients: patientsRes.data,
              groupes: groupesRes.data
            },
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('Could not cache notes data:', e);
        }
      } else {
        setNotes([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des notes:', error);
      toast.error('Erreur lors du chargement des notes');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Fetch note statuses immediately
    fetchNoteStatuses();
    
    // Try to load from cache first
    try {
      const cached = sessionStorage.getItem('cachedNotesData');
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        
        if (age < 5 * 60 * 1000) {
          setNotes(data.notes);
          setPatients(data.patients);
          setGroupes(data.groupes);
          setLoading(false);
          loadData(true);
          return;
        }
      }
    } catch (e) {
      console.warn('Could not load cached notes:', e);
    }
    
    // No cache or cache too old, load with loading indicator
    loadData();
  }, []);

  // Fetch note statuses
  const fetchNoteStatuses = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/note-statuses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNoteStatuses(data);
      }
    } catch (error) {
      console.error('Error fetching note statuses:', error);
    }
  };

  // Filter notes with useMemo for proper dependency tracking
  const filteredNotes = useMemo(() => {
    let result = notes;
    
    // Apply filters
    if (searchTerm || selectedPatient !== 'all' || selectedGroupe !== 'all' || selectedStatus !== 'all') {
      const lowerSearchTerm = searchTerm.toLowerCase();
      
      result = notes.filter(note => {
        // Check patient filter first
        const matchesPatient = selectedPatient === 'all' || note.patient_id === selectedPatient;
        if (!matchesPatient) return false;
        
        // Check groupe filter
        const matchesGroupe = selectedGroupe === 'all' || note.patient.groupe_id === selectedGroupe;
        if (!matchesGroupe) return false;
        
        // Check status filter
        const matchesStatus = selectedStatus === 'all' || note.status_id === selectedStatus;
        if (!matchesStatus) return false;
        
        // If no search term, return all that match filters
        if (!searchTerm) return true;
        
        // Get groupe name efficiently using map
        const patientGroupeName = note.patient.groupe_id ? (groupeMap[note.patient.groupe_id] || '') : '';
        
        // Check all searchable fields
        const matchesSearch = 
          (note.titre && note.titre.toLowerCase().includes(lowerSearchTerm)) ||
          (note.contenu && note.contenu.toLowerCase().includes(lowerSearchTerm)) ||
          (note.patient.nom && note.patient.nom.toLowerCase().includes(lowerSearchTerm)) ||
          (note.patient.prenom && note.patient.prenom.toLowerCase().includes(lowerSearchTerm)) ||
          (note.patient.etablissement && note.patient.etablissement.toLowerCase().includes(lowerSearchTerm)) ||
          (note.patient.categorie_libre && note.patient.categorie_libre.toLowerCase().includes(lowerSearchTerm)) ||
          (patientGroupeName && patientGroupeName.toLowerCase().includes(lowerSearchTerm));
        
        return matchesSearch;
      });
    }
    
    // Sort by created_at descending (most recent first)
    return result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [notes, searchTerm, selectedPatient, selectedGroupe, selectedStatus, groupeMap]);

  // Fetch comments for a note
  const fetchCommentsForNote = async (noteId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${noteId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setNoteComments(prev => ({ ...prev, [noteId]: data }));
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // Toggle comments section
  const toggleComments = async (noteId) => {
    const newExpanded = new Set(expandedComments);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
      // Fetch comments when expanding
      if (!noteComments[noteId]) {
        await fetchCommentsForNote(noteId);
      }
    }
    setExpandedComments(newExpanded);
  };

  // Create comment
  const handleCreateComment = async (noteId) => {
    const content = newComment[noteId];
    if (!content || !content.trim()) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notes/${noteId}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note_id: noteId, content })
      });

      if (response.ok) {
        toast.success('Commentaire ajouté');
        setNewComment(prev => ({ ...prev, [noteId]: '' }));
        await fetchCommentsForNote(noteId);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
  };

  // Update comment
  const handleUpdateComment = async (commentId, noteId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: editCommentContent })
      });

      if (response.ok) {
        toast.success('Commentaire modifié');
        setEditingCommentId(null);
        setEditCommentContent('');
        await fetchCommentsForNote(noteId);
      }
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId, noteId) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Commentaire supprimé');
        await fetchCommentsForNote(noteId);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Pagination logic
  const totalItems = filteredNotes.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const showPagination = totalItems > itemsPerPage;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNotes = filteredNotes.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedPatient, selectedGroupe]);

  // Update allExpanded state based on actual expansion
  useEffect(() => {
    const totalExpandableNotes = currentNotes.filter(note => shouldTruncate(note.contenu)).length;
    const expandedExpandableNotes = currentNotes.filter(note => 
      shouldTruncate(note.contenu) && expandedNotes.has(note.id)
    ).length;
    
    setAllExpanded(totalExpandableNotes > 0 && expandedExpandableNotes === totalExpandableNotes);
  }, [expandedNotes, currentNotes]);

  // Create note
  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/notes`, newNote);
      toast.success('Note créée avec succès');
      setIsCreateNoteOpen(false);
      setNewNote({ patient_id: '', titre: '', contenu: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la création de la note');
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette note ?')) {
      try {
        await axios.delete(`${API}/notes/${noteId}`);
        toast.success('Note supprimée avec succès');
        loadData();
      } catch (error) {
        toast.error('Erreur lors de la suppression de la note');
      }
    }
  };

  // Edit note
  const handleEditNote = (note) => {
    // Hide editor first
    setIsEditorReady(false);
    
    // Set the content
    const newEditNote = {
      titre: note.titre || '',
      contenu: note.contenu || '',
      status_id: note.status_id || ''
    };
    setEditNote(newEditNote);
    
    // Show the edit form
    setEditingNoteId(note.id);
    
    // Increment key and show editor after state is set
    setEditKey(prev => prev + 1);
    
    // Show editor after a delay to ensure state is updated
    setTimeout(() => {
      setIsEditorReady(true);
    }, 150);
  };

  // Update note
  const handleUpdateNote = async (e) => {
    e.preventDefault();
    if (!editingNoteId) return;

    try {
      await axios.put(`${API}/notes/${editingNoteId}`, editNote);
      toast.success('Note modifiée avec succès');
      setEditingNoteId(null);
      setEditNote({ titre: '', contenu: '' });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification de la note');
    }
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditNote({ titre: '', contenu: '' });
  };

  // Read more functionality
  const toggleNoteExpansion = (noteId) => {
    setExpandedNotes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noteId)) {
        newSet.delete(noteId);
      } else {
        newSet.add(noteId);
      }
      return newSet;
    });
  };

  const expandAllNotes = () => {
    const expandableNoteIds = currentNotes
      .filter(note => shouldTruncate(note.contenu))
      .map(note => note.id);
    setExpandedNotes(new Set(expandableNoteIds));
  };

  const collapseAllNotes = () => {
    setExpandedNotes(new Set());
  };

  // Helper function to check if content should be truncated
  // Helper to strip HTML tags for length calculation
  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  };

  const shouldTruncate = (content) => {
    const textContent = stripHtml(content);
    return textContent.length > 200; // Truncate if more than 200 characters
  };

  const getTruncatedContent = (content) => {
    const textContent = stripHtml(content);
    if (textContent.length <= 200) return content;
    
    // Find a good truncation point in the HTML
    const tmp = document.createElement('div');
    tmp.innerHTML = content || '';
    const truncated = tmp.textContent.substring(0, 200) + '...';
    return truncated;
  };

  // Get groupe name
  const getGroupeNom = (groupeId) => {
    const groupe = groupes.find(g => g.id === groupeId);
    return groupe ? groupe.nom : 'Aucun groupe';
  };

  // Get groupe color
  const getGroupeCouleur = (groupeId) => {
    const groupe = groupes.find(g => g.id === groupeId);
    return groupe ? groupe.couleur : '#6B7280';
  };

  return (
    <div className="py-6">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Mes Notes</h2>
            <p className="text-gray-600 mt-1">Gérez vos notes et comptes rendus</p>
          </div>
          
          <div className="flex items-center gap-3">
            {filteredNotes.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={allExpanded ? collapseAllNotes : expandAllNotes}
                  className="flex items-center gap-2"
                  data-testid="toggle-all-expansion"
                >
                  {allExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      Tout réduire
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      Tout développer
                    </>
                  )}
                </Button>
              </div>
            )}
            
            <Dialog open={isCreateNoteOpen} onOpenChange={setIsCreateNoteOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2" data-testid="create-note-btn">
                <Plus className="h-4 w-4" />
                Nouvelle Note
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-x-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>Créer une nouvelle note</DialogTitle>
                <DialogDescription>
                  Rédiger une note pour un {getPatientLabel()}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto overflow-x-hidden">
                <form onSubmit={handleCreateNote} className="space-y-4">
                  <div>
                    <Label htmlFor="note-patient">{getPatientLabelCapitalized()} *</Label>
                    <Select value={newNote.patient_id} onValueChange={(value) => setNewNote({...newNote, patient_id: value})} required>
                      <SelectTrigger data-testid="note-patient-select">
                        <SelectValue placeholder={`Selectionner un ${getPatientLabel()}`} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {patients.map(patient => (
                          <SelectItem key={patient.id} value={patient.id}>
                            {patient.prenom} {patient.nom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="note-titre">Titre *</Label>
                    <Input
                      id="note-titre"
                      value={newNote.titre}
                      onChange={(e) => setNewNote({...newNote, titre: e.target.value})}
                      placeholder="Titre de la note..."
                      required
                      data-testid="note-title-input"
                    />
                  </div>

                  {areStatusesEnabled() && noteStatuses.length > 0 && (
                  <div>
                    <Label htmlFor="note-status">Statut</Label>
                    <Select 
                      value={newNote.status_id || 'none'} 
                      onValueChange={(value) => setNewNote({...newNote, status_id: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un statut" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun statut</SelectItem>
                        {noteStatuses.map(status => (
                          <SelectItem key={status.id} value={status.id}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: status.color }}
                              />
                              {status.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Le statut par défaut sera appliqué si aucun n'est sélectionné
                    </p>
                  </div>
                  )}
                  
                  <div>
                    <Label htmlFor="note-contenu">Contenu *</Label>
                    <RichTextEditor
                      value={newNote.contenu}
                      onChange={(value) => setNewNote(prev => ({...prev, contenu: value}))}
                      placeholder="Rédigez votre note ici..."
                    />
                  </div>
                  
                  <div className="flex-shrink-0 pt-4 sticky bottom-0 bg-white">
                    <Button type="submit" className="w-full" data-testid="submit-note-btn">Créer la note</Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Recherche et filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder={`Rechercher (notes, titres, ${getPatientLabelPlural()}, groupes, établissements)...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-notes-input"
                  />
                </div>
              </div>
              <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                <SelectTrigger className="w-64" data-testid="filter-patient-select">
                  <SelectValue placeholder={`Filtrer par ${getPatientLabel()}`} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">Tous les {getPatientLabelPlural()}</SelectItem>
                  {patients.map(patient => (
                    <SelectItem key={patient.id} value={patient.id}>
                      {patient.prenom} {patient.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedGroupe} onValueChange={setSelectedGroupe}>
                <SelectTrigger className="w-64" data-testid="filter-groupe-select">
                  <SelectValue placeholder="Filtrer par groupe" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="all">Tous les groupes</SelectItem>
                  {groupes.map(groupe => (
                    <SelectItem key={groupe.id} value={groupe.id}>
                      {groupe.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-64" data-testid="filter-status-select">
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {noteStatuses.map(status => (
                    <SelectItem key={status.id} value={status.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: status.color }}
                        />
                        {status.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Notes List */}
        <Card>
          <CardHeader>
            <CardTitle>Notes ({filteredNotes.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Chargement...</div>
            ) : filteredNotes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Aucune note trouvée. Créez votre première note pour commencer.
              </div>
            ) : (
              <div>
                {/* Pagination info */}
                {showPagination && (
                  <div className="flex justify-between items-center mb-4 pb-4 border-b">
                    <p className="text-sm text-gray-600">
                      Affichage de {startIndex + 1}-{Math.min(endIndex, totalItems)} sur {totalItems} note(s)
                    </p>
                    <p className="text-sm text-gray-600">
                      Page {currentPage} sur {totalPages}
                    </p>
                  </div>
                )}
                
                <div className="grid gap-4">
                  {currentNotes.map(note => (
                    <div key={note.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`note-${note.id}`}>
                      {editingNoteId === note.id ? (
                        // Edit mode
                        <form onSubmit={handleUpdateNote} className="space-y-4">
                          <div className="flex items-center gap-3 mb-4">
                            <Badge variant="outline">
                              {note.patient.prenom} {note.patient.nom}
                            </Badge>
                            {note.patient.groupe_id && (
                              <Badge 
                                variant="secondary" 
                                style={{backgroundColor: getGroupeCouleur(note.patient.groupe_id) + '20', color: getGroupeCouleur(note.patient.groupe_id)}}
                              >
                                {getGroupeNom(note.patient.groupe_id)}
                              </Badge>
                            )}
                            <div className="text-sm text-gray-600">
                              Créée le {new Date(note.created_at).toLocaleDateString('fr-FR')} à {new Date(note.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor={`edit-titre-${note.id}`}>Titre</Label>
                            <Input
                              id={`edit-titre-${note.id}`}
                              value={editNote.titre}
                              onChange={(e) => setEditNote({...editNote, titre: e.target.value})}
                              required
                              data-testid={`edit-title-${note.id}`}
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor={`edit-contenu-${note.id}`}>Contenu</Label>
                            <RichTextEditor
                              value={editNote.contenu}
                              onChange={(val) => setEditNote(prev => ({...prev, contenu: val}))}
                              placeholder="Rédigez votre note ici..."
                            />
                          </div>

                          {areStatusesEnabled() && noteStatuses.length > 0 && (
                            <div>
                              <Label htmlFor={`edit-status-${note.id}`}>Statut</Label>
                              <Select 
                                value={editNote.status_id || 'none'} 
                                onValueChange={(value) => setEditNote({...editNote, status_id: value === 'none' ? '' : value})}
                              >
                                <SelectTrigger data-testid={`edit-status-${note.id}`}>
                                  <SelectValue placeholder="Sélectionner un statut" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Aucun statut</SelectItem>
                                  {noteStatuses.map(status => (
                                    <SelectItem key={status.id} value={status.id}>
                                      <div className="flex items-center gap-2">
                                        <div 
                                          className="w-3 h-3 rounded-full" 
                                          style={{ backgroundColor: status.color }}
                                        />
                                        {status.name}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          
                          <div className="flex gap-2">
                            <Button type="submit" size="sm" data-testid={`save-note-${note.id}`}>
                              Sauvegarder
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={cancelEdit} data-testid={`cancel-edit-${note.id}`}>
                              Annuler
                            </Button>
                          </div>
                        </form>
                      ) : (
                        // View mode
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{note.titre}</h3>
                              
                              {/* Status badge */}
                              {note.status_id && noteStatuses.find(s => s.id === note.status_id) && (
                                <Badge 
                                  style={{
                                    backgroundColor: noteStatuses.find(s => s.id === note.status_id).color + '20',
                                    color: noteStatuses.find(s => s.id === note.status_id).color,
                                    borderColor: noteStatuses.find(s => s.id === note.status_id).color
                                  }}
                                  className="border"
                                >
                                  {noteStatuses.find(s => s.id === note.status_id).name}
                                </Badge>
                              )}
                              
                              <Badge variant="outline">
                                {note.patient.prenom} {note.patient.nom}
                              </Badge>
                              {note.patient.groupe_id && (
                                <Badge 
                                  variant="secondary" 
                                  style={{backgroundColor: getGroupeCouleur(note.patient.groupe_id) + '20', color: getGroupeCouleur(note.patient.groupe_id)}}
                                >
                                  {getGroupeNom(note.patient.groupe_id)}
                                </Badge>
                              )}
                            </div>
                            
                            <div className="text-sm text-gray-500 mb-3 flex items-center gap-3">
                              <span>
                                📅 Créée le {new Date(note.created_at).toLocaleDateString('fr-FR')} à {new Date(note.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {note.updated_at && new Date(note.updated_at).getTime() !== new Date(note.created_at).getTime() && (
                                <span className="text-gray-400">
                                  • Modifiée le {new Date(note.updated_at).toLocaleDateString('fr-FR')} à {new Date(note.updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                            
                            <div className="text-gray-800">
                              {shouldTruncate(note.contenu) && !expandedNotes.has(note.id) ? (
                                <>
                                  <div className="note-content" dangerouslySetInnerHTML={{ __html: getTruncatedContent(note.contenu) }} />
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => toggleNoteExpansion(note.id)}
                                    className="p-0 h-auto text-blue-600 hover:text-blue-800 ml-2"
                                    data-testid={`read-more-${note.id}`}
                                  >
                                    Lire plus
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <div 
                                    className="note-content overflow-hidden" 
                                    dangerouslySetInnerHTML={{ __html: note.contenu }}
                                  />
                                  {shouldTruncate(note.contenu) && (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      onClick={() => toggleNoteExpansion(note.id)}
                                      className="p-0 h-auto text-blue-600 hover:text-blue-800 ml-2 block mt-2"
                                      data-testid={`read-less-${note.id}`}
                                    >
                                      Lire moins
                                    </Button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleEditNote(note)}
                              className="text-blue-600 hover:text-blue-700"
                              data-testid={`edit-note-${note.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-note-${note.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Comments Section */}
                      <div className="mt-4 pt-4 border-t">
                        <button
                          onClick={() => toggleComments(note.id)}
                          className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          <MessageSquare className="h-4 w-4" />
                          <span>
                            {expandedComments.has(note.id) 
                              ? `Masquer les commentaires (${noteComments[note.id]?.length || 0})` 
                              : `Afficher les commentaires (${noteComments[note.id]?.length || 0})`
                            }
                          </span>
                          {expandedComments.has(note.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        
                        {expandedComments.has(note.id) && (
                          <div className="mt-4 space-y-4">
                            <div className="space-y-2">
                              <RichTextEditor
                                value={newComment[note.id] || ''}
                                onChange={(value) => setNewComment(prev => ({ ...prev, [note.id]: value }))}
                                placeholder="Ajouter un commentaire..."
                              />
                              <Button
                                size="sm"
                                onClick={() => handleCreateComment(note.id)}
                                disabled={!newComment[note.id]?.trim()}
                              >
                                Envoyer
                              </Button>
                            </div>
                            
                            {noteComments[note.id] && noteComments[note.id].length > 0 ? (
                              <div className="space-y-3">
                                {noteComments[note.id].map(comment => (
                                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <span className="font-medium text-sm">{comment.author_name}</span>
                                        <span className="text-xs text-gray-500 ml-2">
                                          {new Date(comment.created_at).toLocaleDateString('fr-FR')}
                                        </span>
                                      </div>
                                      {comment.user_id === user?.id && (
                                        <div className="flex gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              setEditingCommentId(comment.id);
                                              setEditCommentContent(comment.content);
                                            }}
                                          >
                                            <Edit className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteComment(comment.id, note.id)}
                                            className="text-red-600"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {editingCommentId === comment.id ? (
                                      <div className="space-y-2">
                                        <RichTextEditor
                                          value={editCommentContent}
                                          onChange={setEditCommentContent}
                                          key={`edit-comment-${comment.id}`}
                                        />
                                        <div className="flex gap-2">
                                          <Button size="sm" onClick={() => handleUpdateComment(comment.id, note.id)}>
                                            Sauvegarder
                                          </Button>
                                          <Button size="sm" variant="outline" onClick={() => {
                                            setEditingCommentId(null);
                                            setEditCommentContent('');
                                          }}>
                                            Annuler
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: comment.content }} />
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 text-center py-2">
                                Aucun commentaire
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Pagination controls */}
                {showPagination && (
                  <div className="flex justify-center items-center gap-4 mt-6 pt-4 border-t">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      data-testid="notes-pagination-prev"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Précédent
                    </Button>
                    
                    <span className="text-sm font-medium">
                      Page {currentPage} sur {totalPages}
                    </span>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      data-testid="notes-pagination-next"
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};


export default MesNotes;
